import React, { useRef, useEffect } from 'react';
import cn from 'classnames';
import Collapse from 'bootstrap/js/dist/collapse';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface IAccordionFeatures {
	image?: IImage;
	text?: string;
}

export interface IAccordionItem {
	/**
	 * Accordion Item Title
	 */
	title?: string;
	/**
	 * Accordion Item Content
	 * @control richtext
	 */
	content?: string;
	/**
	 * Accordion Item Featured Content
	 */
	features?: IAccordionFeatures[];
}

export interface IAccordionProps extends IBaseElementProps {
	items?: IAccordionItem[];
}

/**
 * Accordion
 * @block
 * @requiresHooks
 * @icon plus-alt
 */
const Accordion: React.FC<IAccordionProps> = ({
	items,
	className,
	classNames = [],
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		(
			ref.current.querySelectorAll(
				'.accordion__item',
			) as NodeListOf<HTMLDivElement>
		).forEach((item) => {
			const btn = item.querySelector(
				'.accordion__item-title',
			) as HTMLButtonElement;
			const el = item.querySelector(
				'.accordion__item-content',
			) as HTMLDivElement;

			const collapse = new Collapse(el, {
				toggle: false,
			});

			el.addEventListener('show.bs.collapse', () => {
				el.parentElement.classList.add('expanded');
			});

			el.addEventListener('hide.bs.collapse', () => {
				el.parentElement.classList.remove('expanded');
			});

			btn.addEventListener('click', (e) => {
				e.preventDefault();
				collapse.toggle();
			});
		});
	}, []);

	return (
		<div className={className || cn('accordion', ...classNames)} ref={ref}>
			{(items || []).map((item, index) => (
				<div className="accordion__item" key={index}>
					<button type="button" className="accordion__item-title">
						{item.title}
					</button>
					<div className="accordion__item-content collapse">
						<div className="accordion__item-content__inner">
							{item.content && (
								<div
									dangerouslySetInnerHTML={{
										__html: item.content,
									}}
								/>
							)}
							{item.features && item.features.length > 0 && (
								<div className="accordion__item-features">
									{item.features.map((feature, fIndex) => (
										<div
											className="accordion__item-feature"
											key={fIndex}
										>
											{feature.image && (
												<div className="accordion__item-feature-image">
													<Image {...feature.image} />
												</div>
											)}
											{feature.text && (
												<div className="accordion__item-feature-text">
													{feature.text}
												</div>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			))}
		</div>
	);
};

export default Accordion;
